import React, { useEffect, useRef, useState } from 'react';
import Editor from '@monaco-editor/react';
import { AlertTriangle } from 'lucide-react';
import { useTheme } from 'shared/theme/ThemeContext';

/** Pretty-print a value the way the editor should show it. null/undefined → ''. */
function stringifyPretty(v) {
  if (v === null || v === undefined) return '';
  try { return JSON.stringify(v, null, 2); } catch (_) { return ''; }
}

/**
 * A raw-JSON editor over Monaco.
 *
 * Model config, tools and response_format are edited here as literal JSON — the
 * exact shape the SDK returns from `compile()` — rather than through bespoke
 * form controls. The value flows up only while the text parses; an in-progress
 * edit that isn't valid JSON yet is held locally and flagged, so a half-typed
 * object never clobbers the draft.
 *
 * `emptyValue` is what an empty editor means for this field: `{}` for model
 * config, `[]` for tools, `null` for an optional response_format.
 */
export default function JsonEditor({
  value, onChange, disabled, emptyValue = null, height = '220px',
}) {
  const { theme } = useTheme();
  const [text, setText] = useState(() => stringifyPretty(value));
  const [error, setError] = useState(null);
  // The last JSON we emitted upward, so an echo of our own change doesn't reset
  // the editor mid-keystroke — only a genuinely external value (loading another
  // version) does.
  const lastEmitted = useRef(stringifyPretty(value));

  useEffect(() => {
    const incoming = stringifyPretty(value);
    if (incoming !== lastEmitted.current) {
      setText(incoming);
      lastEmitted.current = incoming;
      setError(null);
    }
  }, [value]);

  const handle = (next) => {
    const t = next ?? '';
    setText(t);
    const trimmed = t.trim();
    if (trimmed === '') {
      setError(null);
      lastEmitted.current = '';
      onChange(emptyValue);
      return;
    }
    try {
      const parsed = JSON.parse(trimmed);
      setError(null);
      lastEmitted.current = stringifyPretty(parsed);
      onChange(parsed);
    } catch (e) {
      // Hold the text, keep the last good value in the draft, surface the error.
      setError(e.message.replace(/^JSON\.parse: /, ''));
    }
  };

  return (
    <div>
      <div
        className={`overflow-hidden rounded-md border bg-[color:var(--surface-base)] ${
          error ? 'border-[color:var(--feedback-error)]' : 'border-[color:var(--border-default)]'
        }`}
      >
        <Editor
          height={height}
          language="json"
          value={text}
          onChange={handle}
          theme={theme === 'dark' ? 'vs-dark' : 'vs'}
          options={{
            readOnly: disabled,
            fontSize: 12.5,
            fontFamily: 'JetBrains Mono, Menlo, Consolas, monospace',
            lineNumbers: 'on',
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            insertSpaces: true,
            wordWrap: 'on',
            renderLineHighlight: 'line',
            bracketPairColorization: { enabled: true },
            folding: true,
            padding: { top: 8, bottom: 8 },
          }}
        />
      </div>
      {error && (
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[color:var(--feedback-error)]">
          <AlertTriangle size={12} className="shrink-0" /> Invalid JSON: {error}
        </div>
      )}
    </div>
  );
}
